import { ComputedRoute, RouteService } from '@geovelo-frontends/commons';
import {
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  TextField,
} from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AppContext } from '../../context';
import Button from '../button';
import Dialog from '../dialog';

type TProps = Omit<DialogProps, 'onClose'> & {
  computedRoute: ComputedRoute;
  onClose: (id?: number) => void;
};

function SaveRouteDialog({ computedRoute, onClose, ...props }: TProps): JSX.Element {
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const {
    user: { routes: userRoutes },
    actions: { setUserRoutes },
  } = useContext(AppContext);
  const { t } = useTranslation();

  useEffect(() => {
    if (props.open) setTitle('');
  }, [props.open]);

  async function handleSubmit() {
    if (!title) return;

    setLoading(true);

    try {
      const route = await RouteService.saveRoute({
        type: 'USER',
        computedRouteId: computedRoute.id,
        description: title,
      });

      if (userRoutes) setUserRoutes([route, ...userRoutes]);
      onClose(route.id);
    } catch (err) {
      console.error(err);
    }

    setLoading(false);
  }

  return (
    <Dialog
      fullWidth
      disableEscapeKeyDown={loading}
      maxWidth="xs"
      onClose={(_, reason) => (!loading || reason !== 'backdropClick') && onClose()}
      {...props}
    >
      <DialogTitle>{t('geovelo.routing.save_dialog.title')}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          disabled={loading}
          label={t('commons.title')}
          margin="dense"
          onChange={({ target: { value } }) => setTitle(value)}
          size="small"
          value={title || ''}
          variant="outlined"
        />
      </DialogContent>
      <DialogActions>
        <Button disabled={loading} onClick={() => onClose()} variant="outlined">
          {t('commons.actions.cancel')}
        </Button>
        <Button
          color="primary"
          disabled={loading || !title}
          onClick={handleSubmit}
          startIcon={loading && <CircularProgress color="inherit" size={16} thickness={4} />}
          variant="contained"
        >
          {t('commons.actions.add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SaveRouteDialog;
